import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import './Access.css';

const EmployeeForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin, user } = useContext(AuthContext);

  const canSave = isEdit ? hasPermission('employees.update') : hasPermission('employees.create');
  const isSelf = isEdit && user && String(user.id) === String(id);

  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    phone: '',
    role: '',
    isActive: true,
    isSuperAdmin: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        // Only active roles can be assigned to someone new
        const rolesResponse = await axios.get(`${API_BASE_URL}/admin/roles`);
        setRoles(rolesResponse.data?.data || []);

        if (isEdit) {
          const response = await axios.get(`${API_BASE_URL}/admin/employees/${id}`);
          const employee = response.data?.data;
          if (employee) {
            setForm({
              username: employee.username || '',
              password: '',
              name: employee.name || '',
              email: employee.email || '',
              phone: employee.phone || '',
              role: employee.role?.id || '',
              isActive: employee.isActive !== false,
              isSuperAdmin: Boolean(employee.isSuperAdmin)
            });
          }
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load employee');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.username.trim()) {
      setError('Username is required');
      return;
    }
    if (!isEdit && form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (isEdit && form.password && form.password.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (!form.isSuperAdmin && !form.role) {
      setError('Please assign a role to this employee');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        username: form.username.trim(),
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.isSuperAdmin ? form.role || null : form.role,
        isActive: form.isActive
      };
      // Only send the password when one was actually typed
      if (form.password) payload.password = form.password;
      // Only super admins may set this flag at all
      if (isSuperAdmin) payload.isSuperAdmin = form.isSuperAdmin;

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/employees/${id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/admin/employees`, payload);
      }
      navigate('/sy-admin/employees');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = roles.find((role) => role._id === form.role);

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="access-form-card">
            <div className="skeleton skeleton-text" style={{ width: 240, height: 24, marginBottom: 16 }} />
            <div className="skeleton skeleton-text" style={{ width: '100%', height: 120 }} />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="access-header">
          <div>
            <h1>{isEdit ? 'Edit Employee' : 'Add Employee'}</h1>
            <p>Give a staff member a login and the role that fits their job.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/sy-admin/employees')}>
            Back to Employees
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="access-form-card">
            <h2>Account</h2>
            <div className="access-form-grid">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="e.g., riya.desk"
                  autoComplete="off"
                  disabled={!canSave}
                  required
                />
              </div>
              <div className="form-group">
                <label>{isEdit ? 'New Password' : 'Password *'}</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={isEdit ? 'Leave blank to keep current password' : 'At least 6 characters'}
                  autoComplete="new-password"
                  disabled={!canSave}
                  required={!isEdit}
                />
              </div>
            </div>
          </div>

          <div className="access-form-card">
            <h2>Profile</h2>
            <div className="access-form-grid">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Riya Sharma"
                  disabled={!canSave}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="name@company.com"
                  disabled={!canSave}
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="10-digit number"
                  disabled={!canSave}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={form.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setForm({ ...form, isActive: e.target.value === 'active' })}
                  disabled={!canSave || isSelf}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {isSelf && <p className="access-hint">You cannot deactivate your own account.</p>}
              </div>
            </div>
          </div>

          <div className="access-form-card">
            <h2>Access</h2>

            {isSuperAdmin && (
              <div className="form-group">
                <label className="permission-checkbox" style={{ fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={form.isSuperAdmin}
                    onChange={(e) => setForm({ ...form, isSuperAdmin: e.target.checked })}
                    disabled={!canSave}
                  />
                  <span>Super admin (full access to everything)</span>
                </label>
                <p className="access-hint">
                  Super admins bypass every permission check and can manage roles and employees.
                </p>
              </div>
            )}

            <div className="form-group">
              <label>Role {form.isSuperAdmin ? '' : '*'}</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                disabled={!canSave || form.isSuperAdmin}
              >
                <option value="">{form.isSuperAdmin ? 'Not required for super admins' : 'Select a role'}</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id} disabled={!role.isActive}>
                    {role.name}{role.isActive ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
            </div>

            {selectedRole && !form.isSuperAdmin && (
              <div className="permission-module">
                <div className="permission-module-header">
                  <span className="permission-module-title">
                    {selectedRole.name} grants {selectedRole.permissions?.length || 0} permission(s)
                  </span>
                </div>
                <p className="access-muted" style={{ margin: 0 }}>
                  {selectedRole.description || 'No description'}
                </p>
              </div>
            )}
          </div>

          <div className="access-form-footer">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/sy-admin/employees')}>
              Cancel
            </button>
            {canSave && (
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : isEdit ? 'Update Employee' : 'Create Employee'}
              </button>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EmployeeForm;
